import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ColorEmotion {
  color: string;
  hex: string;
  chinese: string;
  english: string;
  emotion: string;
  icon: string;
}

interface MarketingColor {
  brand: string;
  color: string;
  hex: string;
  reason: string;
}

interface CulturalDifference {
  color: string;
  hex: string;
  chinese: string;
  western: string;
}

interface ColorPsychTemplateProps {
  topic: string;
  subtitle: string;
  colorEmotions: ColorEmotion[];
  marketingColors: MarketingColor[];
  culturalDiffs: CulturalDifference[];
  colorWheelSegments: { label: string; hex: string; angle: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: ColorPsychTemplateProps = {
  topic: '色彩心理学与感知',
  subtitle: 'Color Psychology & Perception',
  colorEmotions: [
    {
      color: '红色',
      hex: '#ef4444',
      chinese: '激情',
      english: 'Passion',
      emotion: '兴奋、紧迫、力量',
      icon: '❤️',
    },
    {
      color: '蓝色',
      hex: '#3b82f6',
      chinese: '信任',
      english: 'Trust',
      emotion: '冷静、可靠、专业',
      icon: '💎',
    },
    {
      color: '绿色',
      hex: '#22c55e',
      chinese: '自然',
      english: 'Nature',
      emotion: '和谐、健康、成长',
      icon: '🌿',
    },
    {
      color: '紫色',
      hex: '#a855f7',
      chinese: '神秘',
      english: 'Mystery',
      emotion: '高贵、创造、智慧',
      icon: '👑',
    },
    {
      color: '黄色',
      hex: '#eab308',
      chinese: '乐观',
      english: 'Optimism',
      emotion: '快乐、温暖、注意',
      icon: '🌟',
    },
    {
      color: '橙色',
      hex: '#f97316',
      chinese: '活力',
      english: 'Vitality',
      emotion: '友好、自信、冒险',
      icon: '🔥',
    },
  ],
  marketingColors: [
    { brand: '可口可乐', color: '红色', hex: '#ef4444', reason: '激发渴望与兴奋' },
    { brand: 'Facebook', color: '蓝色', hex: '#3b82f6', reason: '传递信任与连接' },
    { brand: '星巴克', color: '绿色', hex: '#22c55e', reason: '自然与放松体验' },
    { brand: '麦当劳', color: '黄+红', hex: '#eab308', reason: '刺激食欲与快乐' },
  ],
  culturalDiffs: [
    {
      color: '红色',
      hex: '#ef4444',
      chinese: '喜庆、好运、繁荣',
      western: '危险、爱情、警告',
    },
    {
      color: '白色',
      hex: '#e5e7eb',
      chinese: '哀悼、丧事',
      western: '纯洁、婚礼',
    },
    {
      color: '黄色',
      hex: '#eab308',
      chinese: '皇权、尊贵',
      western: '警告、快乐',
    },
    {
      color: '黑色',
      hex: '#374151',
      chinese: '庄重、神秘',
      western: '优雅、正式',
    },
  ],
  colorWheelSegments: [
    { label: '红', hex: '#ef4444', angle: 0 },
    { label: '橙', hex: '#f97316', angle: 30 },
    { label: '黄', hex: '#eab308', angle: 60 },
    { label: '黄绿', hex: '#84cc16', angle: 90 },
    { label: '绿', hex: '#22c55e', angle: 120 },
    { label: '青', hex: '#06b6d4', angle: 150 },
    { label: '蓝', hex: '#3b82f6', angle: 180 },
    { label: '靛', hex: '#6366f1', angle: 210 },
    { label: '紫', hex: '#a855f7', angle: 240 },
    { label: '玫', hex: '#d946ef', angle: 270 },
    { label: '粉', hex: '#ec4899', angle: 300 },
    { label: '绯', hex: '#f43f5e', angle: 330 },
  ],
  stats: [
    { value: '90%', label: '产品第一印象来自颜色' },
    { value: '85%', label: '消费者因颜色决定购买' },
    { value: '80%', label: '品牌辨识度源于色彩' },
  ],
  bottomQuote:
    '色彩是一种无声的语言，在我们意识到之前就已经影响了我们的情绪、决策与行为',
};

export const ColorPsychTemplate: React.FC<
  Partial<ColorPsychTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const emotionStart = 30;
  const wheelStart = 160;
  const marketStart = 280;
  const cultureStart = 370;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const wheelDraw = interpolate(frame, [wheelStart, wheelStart + 100], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating colored circles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 367) % 1920;
        const y = (i * 209) % 1080;
        const size = 4 + (i % 7) * 2;
        const drift = Math.sin(frame * 0.025 + i * 0.9) * 10;
        const driftX = Math.cos(frame * 0.02 + i * 0.7) * 7;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.5);
        const colors = [
          '#ef4444', '#3b82f6', '#22c55e', '#a855f7',
          '#eab308', '#f97316', '#ec4899', '#06b6d4',
        ];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: 'linear-gradient(135deg, #ef4444, #3b82f6, #22c55e)',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          🎨 色彩心理
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            background: 'linear-gradient(90deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #a855f7)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== COL 1: Color Emotions ===== */}
      <div style={{ position: 'absolute', top: 160, left: 36, width: 416 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 15,
            letterSpacing: '0.08em',
          }}
        >
          色彩与情感对应
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {p.colorEmotions.map((ce, i) => {
            const s = spring({
              frame: frame - (emotionStart + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#0f0f1a',
                  borderRadius: 14,
                  padding: '10px 16px',
                  border: `1.5px solid ${ce.hex}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: ce.hex,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    flexShrink: 0,
                    boxShadow: `0 0 20px ${ce.hex}50`,
                  }}
                >
                  {ce.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}
                  >
                    <span
                      style={{ fontSize: 26, fontWeight: 800, color: ce.hex }}
                    >
                      {ce.color}
                    </span>
                    <span style={{ fontSize: 24, color: '#e5e7eb', fontWeight: 700 }}>
                      {ce.chinese}
                    </span>
                    <span style={{ fontSize: 22, color: '#64748b' }}>
                      {ce.english}
                    </span>
                  </div>
                  <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                    {ce.emotion}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== COL 2: Color Wheel SVG ===== */}
      <div style={{ position: 'absolute', top: 160, left: 472, width: 540 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 15,
            letterSpacing: '0.08em',
          }}
        >
          色彩轮
        </div>
        <svg width="540" height="540" viewBox="0 0 540 540">
          <defs>
            <filter id="colorGlow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Color wheel segments */}
          {p.colorWheelSegments.map((seg, i) => {
            const segAngle = 30;
            const startAngle = ((seg.angle - segAngle / 2) * Math.PI) / 180;
            const endAngle = ((seg.angle + segAngle / 2) * Math.PI) / 180;
            const cx = 270;
            const cy = 235;
            const innerR = 75;
            const outerR = 175;

            const x1 = cx + outerR * Math.cos(startAngle);
            const y1 = cy + outerR * Math.sin(startAngle);
            const x2 = cx + outerR * Math.cos(endAngle);
            const y2 = cy + outerR * Math.sin(endAngle);
            const x3 = cx + innerR * Math.cos(endAngle);
            const y3 = cy + innerR * Math.sin(endAngle);
            const x4 = cx + innerR * Math.cos(startAngle);
            const y4 = cy + innerR * Math.sin(startAngle);

            const segS = spring({
              frame: frame - (wheelStart + i * 6),
              fps,
              config: { damping: 14, stiffness: 120 },
            });

            const labelAngle = (seg.angle * Math.PI) / 180;
            const labelR = outerR + 28;

            return (
              <g key={i} opacity={segS}>
                <path
                  d={`M${x1},${y1} A${outerR},${outerR} 0 0,1 ${x2},${y2} L${x3},${y3} A${innerR},${innerR} 0 0,0 ${x4},${y4} Z`}
                  fill={seg.hex}
                  opacity={0.7 + 0.3 * wheelDraw}
                  filter="url(#colorGlow)"
                />
                <text
                  x={cx + labelR * Math.cos(labelAngle)}
                  y={cy + labelR * Math.sin(labelAngle) + 6}
                  fill={seg.hex}
                  fontSize="20"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {seg.label}
                </text>
              </g>
            );
          })}

          {/* Center circle */}
          <circle cx="270" cy="235" r="58" fill="#1a1a2e" stroke="#33334d" strokeWidth="2" />
          <text x="270" y="229" fill="#e5e7eb" fontSize="22" fontWeight="700" textAnchor="middle">
            色彩
          </text>
          <text x="270" y="251" fill="#94a3b8" fontSize="16" textAnchor="middle">
            Color Wheel
          </text>

          {/* Complementary color lines */}
          <g
            opacity={interpolate(frame, [wheelStart + 70, wheelStart + 90], [0, 0.4], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            <line x1="95" y1="235" x2="445" y2="235" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3" />
            <line x1="170" y1="85" x2="370" y2="385" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3" />
            <line x1="370" y1="85" x2="170" y2="385" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3" />
          </g>

          {/* Color harmony types - horizontal row below wheel */}
          <g
            opacity={interpolate(frame, [wheelStart + 80, wheelStart + 110], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            {['互补色', '类似色', '三角色', '分裂互补'].map((label, i) => {
              const labelW = 118;
              const gap = 14;
              const totalW = 4 * labelW + 3 * gap;
              const startX = (540 - totalW) / 2;
              const lx = startX + i * (labelW + gap);
              return (
                <g key={i}>
                  <rect x={lx} y={478} width={labelW} height={44} rx="10" fill="#1a1a2e" stroke="#33334d" strokeWidth="1" />
                  <text x={lx + labelW / 2} y={506} fill="#e5e7eb" fontSize="19" fontWeight="700" textAnchor="middle">
                    {label}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {/* ===== COL 3: Marketing ===== */}
      <div style={{ position: 'absolute', top: 160, left: 1032, width: 416 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 15,
            letterSpacing: '0.08em',
          }}
        >
          品牌色彩策略
        </div>
        {p.marketingColors.map((mc, i) => {
          const s = spring({
            frame: frame - (marketStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f0f1a',
                borderRadius: 12,
                padding: '12px 16px',
                border: `1.5px solid ${mc.hex}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: 8,
                  background: mc.hex,
                  flexShrink: 0,
                  boxShadow: `0 0 14px ${mc.hex}40`,
                }}
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: mc.hex }}>
                    {mc.brand}
                  </span>
                  <span style={{ fontSize: 23, color: '#64748b' }}>
                    {mc.color}
                  </span>
                </div>
                <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                  {mc.reason}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== COL 4: Cross-Cultural Differences ===== */}
      <div style={{ position: 'absolute', top: 160, left: 1468, width: 416 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 15,
            letterSpacing: '0.08em',
          }}
        >
          跨文化色彩差异
        </div>
        {p.culturalDiffs.map((cd, i) => {
          const s = spring({
            frame: frame - (cultureStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f0f1a',
                borderRadius: 12,
                padding: '12px 16px',
                border: `1.5px solid ${cd.hex}25`,
                marginBottom: 8,
                opacity: s,
                transform: `translateX(${(1 - s) * 20}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: cd.hex,
                  }}
                />
                <span style={{ fontSize: 26, fontWeight: 800, color: cd.hex }}>
                  {cd.color}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16}}>
                <div style={{ flex: 1 }}>
                  <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 20 }}>🇨🇳 </span>
                  <span style={{ color: '#94a3b8', fontSize: 18 }}>{cd.chinese}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ color: '#3b82f6', fontWeight: 700, fontSize: 20 }}>🌍 </span>
                  <span style={{ color: '#94a3b8', fontSize: 18 }}>{cd.western}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 125,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          const statColors = ['#ef4444', '#3b82f6', '#22c55e'];
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f0f1a',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: `1px solid ${statColors[i % statColors.length]}30`,
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  fontSize: 34,
                  fontWeight: 900,
                  color: statColors[i % statColors.length],
                }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: 'linear-gradient(135deg, rgba(239,68,68,0.12), rgba(59,130,246,0.12), rgba(168,85,247,0.12))',
          borderRadius: 14,
          padding: '18px 28px',
          border: '1.5px solid rgba(255,255,255,0.15)',
          borderTop: '3px solid',
          borderImage: 'linear-gradient(90deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #a855f7) 1',
          opacity: quoteOpacity,
          boxShadow: '0 0 30px rgba(139,92,246,0.15), inset 0 0 30px rgba(255,255,255,0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 28 }}>🎨</span>
          <span
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: '#ffffff',
              lineHeight: 1.6,
              letterSpacing: '0.02em',
              textShadow: '0 0 12px rgba(255,255,255,0.4), 0 1px 3px rgba(0,0,0,0.5)',
            }}
          >
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background:
            'linear-gradient(90deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #a855f7)',
        }}
      />
    </AbsoluteFill>
  );
};
